import { useMemo } from 'react';
import { STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { StudioSongFacts } from './details/props';
import {
  buildStudioItems,
  resolveStudioStyle,
  StudioDetails,
  studioDefaultInstructions,
  themeState,
  withStudioInstructions,
  withStudioStyle,
} from './studioRules';

interface UseStudioItemsProps {
  themeId: StudioThemeId | undefined;
  details: StudioDetails;
  song: StudioSongFacts;
  releaseThemeId: ReleaseThemeId;
}

/**
 * What step 3 shows and "Make it" posts: the state of the asset type for the song, the style, the
 * default and the current Instructions, and the items (null while a field is missing or the asset
 * type is not open for the song).
 */
export function useStudioItems({ themeId, details, song, releaseThemeId }: UseStudioItemsProps) {
  const state = themeId === undefined ? null : themeState(STUDIO_THEMES[themeId], song);
  const style = useMemo(
    () => (themeId === undefined ? null : resolveStudioStyle(releaseThemeId, themeId, details, song.canUseAiReels)),
    [details, releaseThemeId, song.canUseAiReels, themeId]
  );
  const lyricsSynced = song.lyricSyncState === 'synced';
  const defaultInstructions = useMemo(
    () =>
      themeId === undefined ? '' : studioDefaultInstructions(releaseThemeId, themeId, details, style, lyricsSynced),
    [details, lyricsSynced, releaseThemeId, style, themeId]
  );
  const instructions = details.instructions ?? defaultInstructions;
  const items = useMemo(
    () =>
      themeId === undefined || state !== 'open'
        ? null
        : withStudioInstructions(
            withStudioStyle(buildStudioItems(themeId, details, song), style),
            instructions,
            releaseThemeId
          ),
    [details, instructions, releaseThemeId, song, state, style, themeId]
  );
  return { state, style, defaultInstructions, instructions, items };
}
